import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import api from 'api';
import { STATUS } from 'utils/status';
import {
  CreateRunPayload,
  ManualValidationAssetListResponse,
  ManualValidationReportData,
  ManualValidationRun,
  ManualValidationRunDetail,
  ManualValidationRunListResponse,
  ValidateSourceData,
  ValidateSourcePayload,
} from '../../interfaces/manualAssetValidationInterface';

const BASE_PATH = 'project/manual-validation/runs/';

interface ManualAssetValidationState {
  runs: ManualValidationRun[];
  runsCount: number;
  runsNext: string | null;
  runsPrevious: string | null;
  getRunsStatus: STATUS;
  getRunsMessage: string;

  validateSourceStatus: STATUS;
  validateSourceMessage: string;
  validateSourceData: ValidateSourceData | null;

  createRunStatus: STATUS;
  createRunMessage: string;
  createdRun: ManualValidationRunDetail | null;

  runDetail: ManualValidationRunDetail | null;
  getRunDetailStatus: STATUS;
  getRunDetailMessage: string;

  refreshRunStatus: STATUS;
  refreshRunMessage: string;

  reportStatus: STATUS;
  reportMessage: string;
  reportUrl: string | null;

  assets: ManualValidationAssetListResponse['results'];
  assetsCount: number;
  assetsNext: string | null;
  assetsPrevious: string | null;
  getRunAssetsStatus: STATUS;
  getRunAssetsMessage: string;
}

const initialState: ManualAssetValidationState = {
  runs: [],
  runsCount: 0,
  runsNext: null,
  runsPrevious: null,
  getRunsStatus: STATUS.NOT_STARTED,
  getRunsMessage: '',

  validateSourceStatus: STATUS.NOT_STARTED,
  validateSourceMessage: '',
  validateSourceData: null,

  createRunStatus: STATUS.NOT_STARTED,
  createRunMessage: '',
  createdRun: null,

  runDetail: null,
  getRunDetailStatus: STATUS.NOT_STARTED,
  getRunDetailMessage: '',

  refreshRunStatus: STATUS.NOT_STARTED,
  refreshRunMessage: '',

  reportStatus: STATUS.NOT_STARTED,
  reportMessage: '',
  reportUrl: null,

  assets: [],
  assetsCount: 0,
  assetsNext: null,
  assetsPrevious: null,
  getRunAssetsStatus: STATUS.NOT_STARTED,
  getRunAssetsMessage: '',
};

export const getValidationRuns = createAsyncThunk(
  'manualAssetValidation/GET_RUNS',
  async (payload: { sub_project_id: string; page?: number; page_size?: number; silent?: boolean }, thunkAPI) => {
    try {
      const { sub_project_id, page, page_size } = payload;
      const queryString = new URLSearchParams({
        sub_project_id,
        ...(page !== undefined && { page: page.toString() }),
        ...(page_size !== undefined && { page_size: page_size.toString() }),
      }).toString();
      const response = await api.get<ManualValidationRunListResponse>(`${BASE_PATH}?${queryString}`);
      if (response.status === 200) return response.data;
      return thunkAPI.rejectWithValue({ message: 'Failed to fetch validation runs' });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const validateManualValidationSource = createAsyncThunk(
  'manualAssetValidation/VALIDATE_SOURCE',
  async (payload: ValidateSourcePayload, thunkAPI) => {
    try {
      const response = await api.post(`${BASE_PATH}validate-source/`, payload);
      if (response.status === 200 && response.data?.status) return response.data.data as ValidateSourceData;
      return thunkAPI.rejectWithValue({ message: response.data?.message || 'Unable to validate source' });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const createValidationRun = createAsyncThunk(
  'manualAssetValidation/CREATE_RUN',
  async (payload: CreateRunPayload, thunkAPI) => {
    try {
      const response = await api.post<ManualValidationRunDetail>(BASE_PATH, payload);
      if (response.status === 201) return response.data;
      return thunkAPI.rejectWithValue({ message: 'Failed to start validation run' });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const getValidationRunDetail = createAsyncThunk(
  'manualAssetValidation/GET_RUN_DETAIL',
  async (payload: { sub_project_id: string; run_id: string }, thunkAPI) => {
    try {
      const { sub_project_id, run_id } = payload;
      const response = await api.get<ManualValidationRunDetail>(`${BASE_PATH}${run_id}/?sub_project_id=${sub_project_id}`);
      if (response.status === 200) return response.data;
      return thunkAPI.rejectWithValue({ message: 'Failed to fetch validation run detail' });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const refreshValidationRun = createAsyncThunk(
  'manualAssetValidation/REFRESH_RUN',
  async (payload: { sub_project_id: string; run_id: string }, thunkAPI) => {
    try {
      const { sub_project_id, run_id } = payload;
      const response = await api.post<ManualValidationRunDetail>(
        `${BASE_PATH}${run_id}/refresh/?sub_project_id=${sub_project_id}`
      );
      if (response.status === 200) return response.data;
      return thunkAPI.rejectWithValue({ message: 'Failed to refresh validation run' });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const getValidationReport = createAsyncThunk(
  'manualAssetValidation/GET_REPORT',
  async (payload: { sub_project_id: string; run_id: string }, thunkAPI) => {
    try {
      const { sub_project_id } = payload;
      const response = await api.get(`${BASE_PATH}${payload.run_id}/report/?sub_project_id=${sub_project_id}`);
      if (response.status === 200 && response.data?.status) return response.data.data as ManualValidationReportData;
      return thunkAPI.rejectWithValue({ message: response.data?.message || 'Report is not ready yet' });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const getValidationRunAssets = createAsyncThunk(
  'manualAssetValidation/GET_RUN_ASSETS',
  async (payload: { sub_project_id: string; run_id: string; page?: number; page_size?: number }, thunkAPI) => {
    try {
      const { sub_project_id, run_id, page, page_size } = payload;
      const queryString = new URLSearchParams({
        sub_project_id,
        ...(page !== undefined && { page: page.toString() }),
        ...(page_size !== undefined && { page_size: page_size.toString() }),
      }).toString();
      const response = await api.get<ManualValidationAssetListResponse>(`${BASE_PATH}${run_id}/assets/?${queryString}`);
      if (response.status === 200) return response.data;
      return thunkAPI.rejectWithValue({ message: 'Failed to fetch validation run assets' });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

const manualAssetValidationSlice = createSlice({
  name: 'manualAssetValidation',
  initialState,
  reducers: {
    resetValidateSourceState(state) {
      state.validateSourceStatus = STATUS.NOT_STARTED;
      state.validateSourceMessage = '';
      state.validateSourceData = null;
    },
    resetCreateRunState(state) {
      state.createRunStatus = STATUS.NOT_STARTED;
      state.createRunMessage = '';
      state.createdRun = null;
    },
    resetReportState(state) {
      state.reportStatus = STATUS.NOT_STARTED;
      state.reportMessage = '';
      state.reportUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getValidationRuns.pending, (state, action) => {
        if (!action.meta.arg.silent) {
          state.getRunsStatus = STATUS.PENDING;
        }
      })
      .addCase(getValidationRuns.fulfilled, (state, action) => {
        state.getRunsStatus = STATUS.SUCCESS;
        // Background polls keep the same array/object references when nothing actually
        // changed, so the table doesn't re-render and flicker on every poll tick.
        const hasChanged =
          !isEqual(state.runs, action.payload.results) ||
          state.runsCount !== action.payload.count ||
          state.runsNext !== action.payload.next ||
          state.runsPrevious !== action.payload.previous;
        if (hasChanged) {
          state.runs = action.payload.results;
          state.runsCount = action.payload.count;
          state.runsNext = action.payload.next;
          state.runsPrevious = action.payload.previous;
        }
      })
      .addCase(getValidationRuns.rejected, (state, action: any) => {
        if (!action.meta.arg.silent) {
          state.getRunsStatus = STATUS.FAILED;
          state.getRunsMessage = action.payload?.message || 'Failed to fetch validation runs';
        }
      })

      .addCase(validateManualValidationSource.pending, (state) => {
        state.validateSourceStatus = STATUS.PENDING;
        state.validateSourceData = null;
      })
      .addCase(validateManualValidationSource.fulfilled, (state, action) => {
        state.validateSourceStatus = STATUS.SUCCESS;
        state.validateSourceData = action.payload;
      })
      .addCase(validateManualValidationSource.rejected, (state, action: any) => {
        state.validateSourceStatus = STATUS.FAILED;
        state.validateSourceMessage = action.payload?.message || 'Unable to validate source';
      })

      .addCase(createValidationRun.pending, (state) => {
        state.createRunStatus = STATUS.PENDING;
      })
      .addCase(createValidationRun.fulfilled, (state, action) => {
        state.createRunStatus = STATUS.SUCCESS;
        state.createdRun = action.payload;
        state.runs = [action.payload, ...state.runs];
        state.runsCount += 1;
      })
      .addCase(createValidationRun.rejected, (state, action: any) => {
        state.createRunStatus = STATUS.FAILED;
        state.createRunMessage = action.payload?.message || 'Failed to start validation run';
      })

      .addCase(getValidationRunDetail.pending, (state) => {
        state.getRunDetailStatus = STATUS.PENDING;
      })
      .addCase(getValidationRunDetail.fulfilled, (state, action) => {
        state.getRunDetailStatus = STATUS.SUCCESS;
        state.runDetail = action.payload;
      })
      .addCase(getValidationRunDetail.rejected, (state, action: any) => {
        state.getRunDetailStatus = STATUS.FAILED;
        state.getRunDetailMessage = action.payload?.message || 'Failed to fetch validation run detail';
      })

      .addCase(refreshValidationRun.pending, (state) => {
        state.refreshRunStatus = STATUS.PENDING;
      })
      .addCase(refreshValidationRun.fulfilled, (state, action) => {
        state.refreshRunStatus = STATUS.SUCCESS;
        state.runDetail = action.payload;
      })
      .addCase(refreshValidationRun.rejected, (state, action: any) => {
        state.refreshRunStatus = STATUS.FAILED;
        state.refreshRunMessage = action.payload?.message || 'Failed to refresh validation run';
      })

      .addCase(getValidationReport.pending, (state) => {
        state.reportStatus = STATUS.PENDING;
      })
      .addCase(getValidationReport.fulfilled, (state, action) => {
        state.reportStatus = STATUS.SUCCESS;
        state.reportUrl = action.payload.url;
      })
      .addCase(getValidationReport.rejected, (state, action: any) => {
        state.reportStatus = STATUS.FAILED;
        state.reportMessage = action.payload?.message || 'Report is not ready yet';
      })

      .addCase(getValidationRunAssets.pending, (state) => {
        state.getRunAssetsStatus = STATUS.PENDING;
      })
      .addCase(getValidationRunAssets.fulfilled, (state, action) => {
        state.getRunAssetsStatus = STATUS.SUCCESS;
        state.assets = action.payload.results;
        state.assetsCount = action.payload.count;
        state.assetsNext = action.payload.next;
        state.assetsPrevious = action.payload.previous;
      })
      .addCase(getValidationRunAssets.rejected, (state, action: any) => {
        state.getRunAssetsStatus = STATUS.FAILED;
        state.getRunAssetsMessage = action.payload?.message || 'Failed to fetch validation run assets';
      });
  },
});

export const { resetValidateSourceState, resetCreateRunState, resetReportState } = manualAssetValidationSlice.actions;
export default manualAssetValidationSlice.reducer;
